import { Fragment, useState, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import 'react-loading-skeleton/dist/skeleton.css';
import toast from 'react-hot-toast';
import { handleGenerateJson, handleGenerateExcel, handleGenerateCsv, handleGenerateWord } from './functions/exportData.js';


export default function ExportDialog({ open, setOpen, files, fileId, extraction, userId, extractionId }) {
    const cancelButtonRef = useRef(null);

    const objectFields = extraction.fields.filter(field => field.type === 'object');

    const arrayFields = extraction.fields.filter(field =>
        field.type === 'array' &&
        field.items &&
        (field.items.type === 'object' || field.items.type === 'string')
    );

    const [fileFormat, setFileFormat] = useState('excel');
    const [selectedArrayField, setSelectedArrayField] = useState('');
    const [selectedObjectFields, setSelectedObjectFields] = useState([]);

    const handleFileExport = async () => {
        try {
            let currentDateTime = new Date().toISOString();
            let formattedDateTime = currentDateTime.slice(0, 10);
            let fileName = 'Extracta LABS - Data Export - ' + formattedDateTime;

            let outputArray = [];

            let currentFileId = fileId;
            let index = files.findIndex((file) => file.id === currentFileId);
            const file = files[index];

            outputArray = [{
                file_name: file.name,
                data: file.result,
            }];

            // Copy results so export never mutates the file shown in the viewer.
            // A missing or invalid result becomes {}.
            outputArray = outputArray.map(item => ({
                file_name: item.file_name,
                data: (item.data && typeof item.data === 'object' && !Array.isArray(item.data))
                    ? { ...item.data }
                    : {},
            }));

            // if the file has more fields than the extraction's fields, remove the extra fields
            outputArray.forEach(item => {
                Object.keys(item.data).forEach(key => {
                    if (!extraction.fields.some(field => field.key === key)) {
                        delete item.data[key];
                    }
                });
            });

            // if the file has less fields than the extraction's fields, add the missing fields with a value of '-'
            extraction.fields.forEach(field => {
                outputArray.forEach(item => {
                    if (!Object.prototype.hasOwnProperty.call(item.data, field.key)) {
                        item.data[field.key] = '-';
                    }
                });
            });

            let extractionFields = extraction.fields;

            // Handle array expansion first if needed
            if ((fileFormat === 'excel' || fileFormat === 'csv') && selectedArrayField) {
                const { expandedOutput, newFields } = expandArrayField(outputArray, selectedArrayField, extractionFields, selectedObjectFields);
                extractionFields = newFields;
                outputArray = expandedOutput;
            }

            // Then handle object field splitting
            if ((fileFormat === 'excel' || fileFormat === 'csv') && selectedObjectFields.length > 0) {
                const { expandedOutput, newFields } = expandObjectFields(outputArray, selectedObjectFields, extractionFields);
                extractionFields = newFields;
                outputArray = expandedOutput;
            }

            switch (fileFormat) {
                case 'json':
                    handleGenerateJson(outputArray, fileName, extractionFields);
                    break;
                case 'excel':
                    handleGenerateExcel(outputArray, fileName, extractionFields);
                    break;
                case 'csv':
                    handleGenerateCsv(outputArray, fileName, extractionFields);
                    break;
                case 'word':
                    handleGenerateWord(outputArray, fileName);
                    break;
                default:
                    break;
            }

            toast.success('File exported successfully!');
            setOpen(false);
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Error exporting file!');
            setOpen(false);
        }
    }

    const expandObjectFields = (outputArray, objectFieldKeys, fields) => {
        let expandedOutput = outputArray;
        let newFields = [];

        // Process fields in order, replacing object fields with their properties
        fields.forEach(field => {
            if (objectFieldKeys.includes(field.key)) {
                // If this is a selected object field, expand its properties
                const properties = field.properties || [];
                const newFieldEntries = properties.map(property => ({
                    description: property.description,
                    key: `${field.key}.${property.key}`,
                    type: property.type,
                }));
                newFields.push(...newFieldEntries);

                // Update the data in the output array for this field
                expandedOutput = expandedOutput.map(item => {
                    const newItem = {
                        ...item,
                        data: (item.data && typeof item.data === 'object' && !Array.isArray(item.data))
                            ? { ...item.data }
                            : {},
                    };
                    const rawObject = newItem.data[field.key];
                    const objectData = (rawObject && typeof rawObject === 'object' && !Array.isArray(rawObject))
                        ? rawObject
                        : {};

                    // Add flattened object properties
                    properties.forEach(property => {
                        const propertyKey = `${field.key}.${property.key}`;
                        newItem.data[propertyKey] = objectData[property.key] || '';
                    });

                    // Remove the original object
                    delete newItem.data[field.key];

                    return newItem;
                });
            } else {
                // If not a selected object field, keep it as is
                newFields.push(field);
            }
        });

        return { expandedOutput, newFields };
    };

    const expandArrayField = (outputArray, arrayFieldKey, fields, selectedObjectFields) => {
        let expandedOutput = [];

        // Find the index of the original array field in the fields array
        const originalFieldIndex = fields.findIndex(field => field.key === arrayFieldKey);

        // Filter out the original array field from the new fields array (but only for object arrays)
        let newFields = fields.filter(field => field.key !== arrayFieldKey);

        // Find the arrayField structure in extraction.fields to get the properties
        const arrayFieldStructure = fields.find(field => field.key === arrayFieldKey);
        let properties = [];

        if (arrayFieldStructure && arrayFieldStructure.items && arrayFieldStructure.items.properties) {
            // Sort the properties based on the 'id' field to maintain the correct order
            properties = arrayFieldStructure.items.properties.sort((a, b) => a.id - b.id);
        }

        // Loop through each item in the outputArray
        outputArray.forEach(item => {
            let data = (item.data && typeof item.data === 'object' && !Array.isArray(item.data))
                ? item.data
                : {};

            // Find the field that corresponds to the chosen array field
            let arrayField = data[arrayFieldKey];

            // If the field is not an array or does not exist, skip
            if (!Array.isArray(arrayField)) {
                expandedOutput.push({ ...item, data }); // Just push the original data if it's not an array
                return;
            }

            // Check if the array contains objects or strings
            if (typeof arrayField[0] === 'object' && !Array.isArray(arrayField[0])) {
                // Case where array items are objects (expand each object's properties into separate columns)
                arrayField.forEach(arrayEntry => {
                    let newRow = { ...data }; // Clone the original data

                    // Remove the arrayFieldKey from the newRow (we're expanding its fields)
                    delete newRow[arrayFieldKey];

                    const entry = (arrayEntry && typeof arrayEntry === 'object' && !Array.isArray(arrayEntry))
                        ? arrayEntry
                        : {};

                    // Spread object keys (from arrayEntry) into multiple columns in the new row using child keys only, keeping the order based on `properties`
                    properties.forEach(property => {
                        const key = property.key;
                        newRow[`${arrayFieldKey}.${key}`] = entry[key];
                    });

                    // Add the expanded row to the output
                    expandedOutput.push({
                        ...item, // Keep the rest of the item info (e.g., file name)
                        data: newRow // Update data with the expanded row
                    });
                });

                // Insert the new fields at the index of the original array field
                const newFieldEntries = properties.map(property => ({
                    description: property.description,
                    key: `${arrayFieldKey}.${property.key}`,
                    type: property.type,
                }));

                // Insert the new field entries at the original index
                newFields.splice(originalFieldIndex, 0, ...newFieldEntries);
            } else if (typeof arrayField[0] === 'string') {
                // Case where array items are strings (create a new row for each string)
                arrayField.forEach(stringValue => {
                    let newRow = { ...data }; // Clone the original data
                    delete newRow[arrayFieldKey]; // Remove the original array field
                    newRow[arrayFieldKey] = stringValue; // Keep the original field name for string values

                    // Add the new row to the expanded output
                    expandedOutput.push({
                        ...item, // Keep the rest of the item info
                        data: newRow // Update data with the single string value
                    });
                });

                // For string arrays, keep the original field name and type
                const originalField = fields.find(field => field.key === arrayFieldKey);
                if (originalField) {
                    const newField = {
                        ...originalField,
                        key: arrayFieldKey, // Keep the original key without .value
                    };
                    newFields.splice(originalFieldIndex, 0, newField);
                }
            }
        });

        return { expandedOutput, newFields }; // Return both the expanded output and the updated fields
    };

    const showWord = () => {
        const userIds = ['WgVLxXREk7eW2aQeFOSfRbuxJbm1', 'wZfRgTE69LO3BMn1gYtH4ovWeif2', 'uCiWcuKd3ebpbcblafD17Nb1ESV2', 'WOc41gZqwzQdewbi5uTdm3W0xlw1'];
        const extractionIds = ['-O8jj-L82xWGj4C8D7Mu', '-O86GTicjQx2F27obn0Y', '-O86NndgLlK1TKcIU1d_', '-O8jmir7skz8jJykD2H6'];
        return userIds.includes(userId) && extractionIds.includes(extractionId);
    }

    const handleObjectFieldChange = (fieldKey) => {
        setSelectedObjectFields(prev => {
            if (prev.includes(fieldKey)) {
                return prev.filter(key => key !== fieldKey);
            } else {
                return [...prev, fieldKey];
            }
        });
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-100" initialFocus={cancelButtonRef} onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-100 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
                                    <h2 className="text-xl font-semibold mb-6">Export Options</h2>

                                    <div className="space-y-6">
                                        {/* File Format Section */}
                                        <div className="space-y-2">
                                            <h3 className="text-sm font-medium text-gray-900">File Format</h3>
                                            <div className="flex space-x-2">
                                                <button
                                                    className={`flex-1 px-4 py-2 border rounded-md ${fileFormat === 'excel' ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300'}`}
                                                    onClick={() => setFileFormat('excel')}
                                                >
                                                    Excel
                                                </button>
                                                <button
                                                    className={`flex-1 px-4 py-2 border rounded-md ${fileFormat === 'csv' ? 'bg-indigo-600 text-white' : 'border-gray-300'}`}
                                                    onClick={() => setFileFormat('csv')}
                                                >
                                                    CSV
                                                </button>
                                                <button
                                                    className={`flex-1 px-4 py-2 border rounded-md ${fileFormat === 'json' ? 'bg-indigo-600 text-white' : 'border-gray-300'}`}
                                                    onClick={() => setFileFormat('json')}
                                                >
                                                    JSON
                                                </button>
                                                {showWord() && (
                                                    <button
                                                        className={`flex-1 px-4 py-2 border rounded-md ${fileFormat === 'word' ? 'bg-indigo-600 text-white' : 'border-gray-300'}`}
                                                        onClick={() => setFileFormat('word')}
                                                    >
                                                        Word
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Array Expansion Section - Only for Excel and CSV */}
                                        {(fileFormat === 'excel' || fileFormat === 'csv') && arrayFields.length > 0 && (
                                            <div className="space-y-2 pt-4 border-t">
                                                <h3 className="text-sm font-medium text-gray-900">Expand Arrays to Multiple Rows</h3>
                                                <p className="text-sm text-gray-500">
                                                    Select an array field to expand its items across multiple rows. Works with both arrays of objects and arrays of strings.
                                                </p>
                                                <select
                                                    value={selectedArrayField}
                                                    onChange={(e) => setSelectedArrayField(e.target.value)}
                                                    className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                >
                                                    <option value="">Don't expand arrays</option>
                                                    {arrayFields.map((field) => (
                                                        <option key={field.key} value={field.key}>
                                                            {field.key}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* Object Splitting Section - Only for Excel and CSV */}
                                        {(fileFormat === 'excel' || fileFormat === 'csv') && objectFields.length > 0 && (
                                            <div className="space-y-2 pt-4 border-t">
                                                <h3 className="text-sm font-medium text-gray-900">Split Objects into Columns</h3>
                                                <p className="text-sm text-gray-500">
                                                    Select object fields to split their properties into separate columns.
                                                </p>
                                                <div className="space-y-2 mt-2">
                                                    {objectFields.map((field) => (
                                                        <div key={field.key} className="flex items-center">
                                                            <input
                                                                type="checkbox"
                                                                id={`object-field-${field.key}`}
                                                                checked={selectedObjectFields.includes(field.key)}
                                                                onChange={() => handleObjectFieldChange(field.key)}
                                                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                            />
                                                            <label
                                                                htmlFor={`object-field-${field.key}`}
                                                                className="ml-2 block text-sm text-gray-900"
                                                            >
                                                                {field.key}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                                        onClick={handleFileExport}
                                    >
                                        Export File
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        ref={cancelButtonRef}
                                        onClick={() => setOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}